import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { auth } from '../lib/api';

const AuthPage = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = React.useState(true);
  const [userType, setUserType] = React.useState('client'); // 'client' or 'photographer'
  const [form, setForm] = React.useState({ name: '', email: '', password: '' });
  const [submitting, setSubmitting] = React.useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.id]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = isLogin
        ? await auth.login({ email: form.email, password: form.password })
        : await auth.register({
            name: form.name,
            email: form.email,
            password: form.password,
            password_confirmation: form.password,
            role: userType,
          });

      localStorage.setItem('pixnest_token', data.token);
      localStorage.setItem('pixnest_user', JSON.stringify(data.user));
      toast.success(isLogin ? 'Welcome back!' : 'Account created!');
      navigate(data.user.role === 'photographer' ? '/photographer/dashboard' : '/client/dashboard');
    } catch (err) {
      const message = err.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url('https://images.unsplash.com/photo-1758607234944-3f423987893d?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-12">
          <Camera className="w-16 h-16 mb-6" />
          <h1 className="font-playfair text-5xl font-bold mb-4">Pixnest</h1>
          <p className="text-xl text-center max-w-md">
            Where photographers and clients connect for unforgettable moments
          </p>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" className="flex lg:hidden items-center space-x-2 mb-8" data-testid="auth-logo">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-2" data-testid="auth-title">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-muted-foreground" data-testid="auth-subtitle">
              {isLogin ? 'Sign in to your account' : 'Join our community of creatives'}
            </p>
          </div>

          <Card className="border-none shadow-lg">
            <CardContent className="pt-6">
              {/* User Type Toggle */}
              {!isLogin && (
                <div className="mb-6" data-testid="user-type-toggle">
                  <label className="text-sm font-medium mb-2 block">I am a:</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={userType === 'client' ? 'default' : 'outline'}
                      onClick={() => setUserType('client')}
                      data-testid="user-type-client"
                    >
                      Client
                    </Button>
                    <Button
                      type="button"
                      variant={userType === 'photographer' ? 'default' : 'outline'}
                      onClick={() => setUserType('photographer')}
                      data-testid="user-type-photographer"
                    >
                      Photographer
                    </Button>
                  </div>
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                {!isLogin && (
                  <div>
                    <label className="text-sm font-medium mb-2 block" htmlFor="name">Full Name</label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={form.name}
                      onChange={handleChange}
                      data-testid="auth-name-input"
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium mb-2 block" htmlFor="email">Email</label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                    data-testid="auth-email-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block" htmlFor="password">Password</label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={handleChange}
                    data-testid="auth-password-input"
                  />
                </div>

                {isLogin && (
                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" data-testid="auth-remember" />
                      <span>Remember me</span>
                    </label>
                    <a href="#" className="text-primary hover:underline" data-testid="auth-forgot-password">
                      Forgot password?
                    </a>
                  </div>
                )}

                <Button type="submit" className="w-full" size="lg" disabled={submitting} data-testid="auth-submit-button">
                  {submitting ? 'Please wait…' : isLogin ? 'Sign In' : 'Create Account'}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm">
                <span className="text-muted-foreground">
                  {isLogin ? "Don't have an account?" : 'Already have an account?'}
                </span>{' '}
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-primary font-medium hover:underline"
                  data-testid="auth-toggle-mode"
                >
                  {isLogin ? 'Sign up' : 'Sign in'}
                </button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-8">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;